/**
 * Pure helpers for client notification schedule occurrences.
 * The backend accepts UTC datetimes only and does not persist schedule intent.
 */

/** Convert a Date or ISO-compatible value to a UTC ISO-8601 string. */
export const toUtcScheduleDate = value => {
    const normalizedValue =
        value instanceof Date ? value : normalizeIncomingUtcString(value);

    const date =
        normalizedValue instanceof Date
            ? normalizedValue
            : new Date(normalizedValue);

    if (Number.isNaN(date.getTime())) throw new Error('Invalid schedule date');

    return date.toISOString();
};

const normalizeIncomingUtcString = value => {
    if (typeof value !== 'string') return value;

    const trimmed = value.trim();
    const hasTimezone = /([zZ]|[+-]\d{2}:\d{2})$/.test(trimmed);

    return hasTimezone ? trimmed : `${trimmed}Z`;
};
/**
 * Normalize occurrence values for an API payload.
 * Duplicates are removed and results are returned in chronological order.
 */
export const normalizeUtcScheduleDates = dates =>
    [...new Set((dates || []).map(toUtcScheduleDate))].sort(
        (left, right) => new Date(left).getTime() - new Date(right).getTime(),
    );

/** Return only future UTC occurrence values. */
export const getFutureUtcScheduleDates = (dates, now = new Date()) => {
    const nowUtc = new Date(toUtcScheduleDate(now)).getTime();

    return normalizeUtcScheduleDates(dates).filter(
        date => new Date(date).getTime() > nowUtc,
    );
};

/**
 * Build the schedule-definition payload expected by the client API.
 * No timezone, recurrence recipe, or authored schedule intent is included.
 */
export const buildScheduleDefinitionPayload = ({
    id = 0,
    notificationId,
    name,
    notificationFrequencyId,
    isEnabled = true,
    notificationSchedules = [],
}) => ({
    id,
    notificationId,
    name,
    notificationFrequencyId,
    isEnabled,
    notificationSchedules: normalizeUtcScheduleDates(notificationSchedules),
});
